import { useEffect, useRef, useState } from 'react'

import { ConfirmInfoStage, type ConfirmInfoState } from '@/models/confirmInfo'
import { useDrivers } from '@/models/driver'
import { checksForCard, FillCard, FillChecksContext, isRowCheck } from '@/models/fillApplication'
import { useInsuranceApplication } from '@/models/insuranceApplication'
import type { Org } from '@/models/user'
import { CoverageCard } from '../App/Customers/FillApplication/CoverageCard'
import { EquipmentCard } from '../App/Customers/FillApplication/EquipmentCard'
import { FillJumpProvider } from '../App/Customers/FillApplication/FillJumpProvider'
import { UnsavedEditsDialog } from '../App/Customers/FillApplication/UnsavedEditsDialog'
import { useBusinessCardForm } from '../App/Customers/FillApplication/useBusinessCardForm'
import { useCoverageCardForm } from '../App/Customers/FillApplication/useCoverageCardForm'
import { useRefreshChecksOnRosterChange } from '../App/Customers/FillApplication/useRefreshChecksOnRosterChange'
import { useUnsavedEditsGuard } from '../App/Customers/FillApplication/useUnsavedEditsGuard'
import { BusinessSummary } from './BusinessSummary'
import { ConfirmationStep } from './ConfirmationStep'
import { ConfirmBusinessCard } from './ConfirmBusinessCard'
import { ConfirmLossRunsCard } from './ConfirmLossRunsCard'
import type { EditedCard } from './confirmSteps'
import { CoverageSummary } from './CoverageSummary'
import { EquipmentSummary } from './EquipmentSummary'
import { LossRunsSummary } from './LossRunsSummary'
import { MissingSummary } from './MissingSummary'
import { ReviewSection } from './ReviewSection'
import { useLossRunUpload } from './useLossRunUpload'
import { useOpenChecks } from './useOpenChecks'
import { useRosterEdits } from './useRosterEdits'

const MAX_DRIVER_CARDS = 5

interface ConfirmInfoFormProps {
  orgId: string
  organisation: Org
  state: ConfirmInfoState
}

/** The whole application on one page: a summary card per part, each opening its form in a dialog, then Confirmation. */
export function ConfirmInfoForm({ orgId, organisation, state }: ConfirmInfoFormProps) {
  const business = useBusinessCardForm(orgId, organisation)
  // The application's own menu: Pet is on it for an owner-operator.
  const { data: fullApplication } = useInsuranceApplication({
    id: organisation.currentInsuranceApplication?.id,
  })
  const menu = fullApplication?.coverageMenu
  const coverage = useCoverageCardForm(orgId, organisation.currentInsuranceApplication, menu)
  // Driver, truck and claim edits save on their own, so the checks follow those lists.
  useRefreshChecksOnRosterChange(orgId)
  const checks = useOpenChecks(state.checks, business, coverage)
  // Closed at first: most people read down the page and confirm. One part opens at a time, in a dialog.
  const [openCard, setOpenCard] = useState<FillCard | null>(null)
  const confirmationRef = useRef<HTMLDivElement>(null)
  // Only its own: a save that ends after another part was opened mustn't close that one.
  const closeCard = (card: FillCard) => setOpenCard((current) => (current === card ? null : current))
  const rosters = useRosterEdits(orgId, openCard === FillCard.EQUIPMENT)
  // Past a handful of drivers, a table reads faster than a column of cards, so the dialog makes room for its rows.
  // The first page settles that; the rosters load the rest.
  const { drivers } = useDrivers({ orgId })
  const manyDrivers = drivers.length > MAX_DRIVER_CARDS
  const lossRuns = useLossRunUpload(orgId, organisation.currentInsuranceApplication?.id)
  // Page order: confirming saves them in this order and stops at the first that fails.
  const edited: EditedCard[] = [
    { card: FillCard.BUSINESS, label: 'Business', form: business },
    { card: FillCard.COVERAGE, label: 'Coverage', form: coverage },
    // Picked reports are unsaved too: leaving warns, and confirming uploads them first.
    { card: FillCard.LOSS_RUNS, label: 'Loss runs', form: lossRuns },
  ].filter((card) => card.form.isDirty)
  const dirtyLabels = edited.map((card) => card.label)
  const blocker = useUnsavedEditsGuard(dirtyLabels)

  // Past the review they came back for the email code, at the foot of the page.
  const arrivedPastReview = useRef(state.stage !== ConfirmInfoStage.REVIEW)
  useEffect(() => {
    if (arrivedPastReview.current) confirmationRef.current?.scrollIntoView({ block: 'start' })
  }, [])

  return (
    <FillJumpProvider onShowCard={setOpenCard}>
      <FillChecksContext.Provider value={checks}>
        <div className="flex flex-col gap-4">
          {state.stage === ConfirmInfoStage.REVIEW && <MissingSummary checks={checks} />}
          <ReviewSection
            title="Business info"
            checks={checksForCard(checks, FillCard.BUSINESS)}
            open={openCard === FillCard.BUSINESS}
            onOpen={() => setOpenCard(FillCard.BUSINESS)}
            onClose={() => closeCard(FillCard.BUSINESS)}
            form={business}
            summary={<BusinessSummary organisation={organisation} />}
          >
            <ConfirmBusinessCard orgId={orgId} organisation={organisation} checks={checks} form={business} />
          </ReviewSection>
          <ReviewSection
            title="Coverage"
            checks={checksForCard(checks, FillCard.COVERAGE)}
            open={openCard === FillCard.COVERAGE}
            onOpen={() => setOpenCard(FillCard.COVERAGE)}
            onClose={() => closeCard(FillCard.COVERAGE)}
            form={coverage}
            summary={<CoverageSummary application={organisation.currentInsuranceApplication} />}
          >
            <CoverageCard checks={checks} form={coverage} plain menu={menu} />
          </ReviewSection>
          <ReviewSection
            title="Equipment and drivers"
            checks={checksForCard(checks, FillCard.EQUIPMENT)}
            open={openCard === FillCard.EQUIPMENT}
            onOpen={() => setOpenCard(FillCard.EQUIPMENT)}
            onClose={() => closeCard(FillCard.EQUIPMENT)}
            form={rosters}
            wide={manyDrivers}
            summary={<EquipmentSummary orgId={orgId} />}
          >
            <EquipmentCard
              orgId={orgId}
              // Each driver or truck shows what it lacks on its own row.
              checks={checks.filter((check) => !isRowCheck(check))}
              eld={state.eld}
              // No "Add business owner as driver" here: the customer adds themselves like any other driver.
              owner={null}
              plain
              hideEldButton
            />
          </ReviewSection>
          <ReviewSection
            title="Loss runs"
            checks={checksForCard(checks, FillCard.LOSS_RUNS)}
            open={openCard === FillCard.LOSS_RUNS}
            onOpen={() => setOpenCard(FillCard.LOSS_RUNS)}
            onClose={() => closeCard(FillCard.LOSS_RUNS)}
            form={lossRuns}
            summary={<LossRunsSummary orgId={orgId} />}
          >
            <ConfirmLossRunsCard checks={checks} upload={lossRuns} />
          </ReviewSection>
          <div ref={confirmationRef} className="mt-4 scroll-mt-24">
            <ConfirmationStep orgId={orgId} state={state} checks={checks} edited={edited} />
          </div>
        </div>
        <UnsavedEditsDialog blocker={blocker} labels={dirtyLabels} />
      </FillChecksContext.Provider>
    </FillJumpProvider>
  )
}
